import { useCallback, useEffect, useRef, useState } from "react";
import req from "../../../../modules/Request";
import * as session from "../../../../modules/Stash/Stash";
import { buildHierarchy } from "../../../../modules/TaskHierarchy/TaskHierarchy";
import { useNotify } from "../../../components/Notify";

function useTasks() {
  const { notify } = useNotify();

  const [ data, setData ] = useState(undefined);
  const [ project, setProject ] = useState(session.getStore('selected-commessa'));
  const [ requireUpdate, setRequireUpdate ] = useState(false);
  const [ range, setRange ] = useState(undefined);
  const [ isLoading, setIsLoading ] = useState(false);
  const hierarchyRef = useRef(buildHierarchy([]));

  const syncData = useCallback(() => {
    setIsLoading(true);

    req("get-pt-list", { selectedCommessa: project === 'Tutto' ? null : project }).then(res => {
      if (res.success) {
        if (res.data.length > 0) {
          const h = buildHierarchy(res.data);

          const newData = res.data.map((task) => {
            const childs = h.getAllDescendants(task.position);

            const hasSubtasks = childs.some((t) => t.record === 'ST');
            const isGroup = task.code?.startsWith('group');

            return {
              ...task,
              showChilds: task.record === 'COM' && project === 'Tutto'
                ? false
                : task.showChilds,
              type: !isGroup && hasSubtasks
                ? 'project'
                : task.type,
              hideBar: isGroup
            };
          });

          const minDate = newData.reduce((acc, task) => {
            if (task.dates) {
              task.dates.forEach((date) => new Date(date.start) < acc && (acc = new Date(date.start)));
            } else if (task.start && new Date(task.start) < acc) {
              acc = new Date(task.start);
            }

            if (task.timesheets) {
              task.timesheets.forEach((timesheet) => new Date(timesheet.date) < acc && (acc = new Date(timesheet.date)));
            }

            return acc;
          }, new Date());

          const maxDate = newData.reduce((acc, task) => {
            if (task.dates) {
              task.dates.forEach((date) => new Date(date.end) > acc && (acc = new Date(date.end)));
            } else if (task.end && new Date(task.end) > acc) {
              acc = new Date(task.end);
            }

            if (task.timesheets) {
              task.timesheets.forEach((timesheet) => new Date(timesheet.date) > acc && (acc = new Date(timesheet.date)));
            }

            return acc;
          }, new Date());

          minDate.setDate(minDate.getDate() - 100);
          maxDate.setDate(maxDate.getDate() + 100);

          hierarchyRef.current = buildHierarchy(newData);
          setData(newData);
          setRange({ from: minDate.toISOString().split('T')[ 0 ], to: maxDate.toISOString().split('T')[ 0 ] });
        } else {
          setData(undefined);
          notify('Errore', "Commessa non esistente.", 'error');
        }
      } else {
        notify(res.error, 'Errore al caricamento dei task.', 'error');
      }

      setIsLoading(false);
    }).catch(() => {
      notify('Errore di rete', 'Impossibile caricare i dati. Riprova.', 'error');
      setIsLoading(false);
    });
  }, [ project ]);

  /* PROJECT CHANGED */
  useEffect(() => {
    if (project !== undefined) {
      syncData();
    }
  }, [ project ]);

  /* UPDATE REQUESTED */
  useEffect(() => {
    if (requireUpdate && !isLoading) {
      syncData();
      setRequireUpdate(false);
    } else if (requireUpdate && isLoading) {
      notify("Attenzione", "C'è già una richiesta in corso.", 'warn');
      setRequireUpdate(false);
    }
  }, [ requireUpdate ]);

  // Rebuild hierarchy whenever data changes
  const setDataWithHierarchy = useCallback((updater) => {
    setData((prevData) => {
      const newData = typeof updater === 'function' ? updater(prevData) : updater;
      if (newData) {
        hierarchyRef.current = buildHierarchy(newData);
      }
      return newData;
    });
  }, []);

  return {
    data,
    setData: setDataWithHierarchy,
    hierarchy: hierarchyRef.current,
    range,
    isLoading,
    project,
    setProject: (proj) => {
      setProject(proj);
      session.addStore('selected-commessa', proj);
      setRequireUpdate(true);
    },
    resync: () => {
      setRequireUpdate(true);
    }
  };
}

export default useTasks;